import { Link } from 'react-router-dom';
import { ArrowRight, ShoppingCart } from 'lucide-react';
import type { Kit } from '@/types';
import { useCartStore } from '@/stores/cartStore';
import { ProductImage } from './ProductImage';
import { PrecioDisplay } from '@/components/ui/PrecioDisplay';
import { cn } from '@/lib/utils';

function kitCategoria(kit: Kit): string {
  const t = (kit.tipo_venta_producto ?? '').toLowerCase();
  if (t.includes('cctv')) return 'cctv';
  if (t.includes('intrusion') || t.includes('intrusión')) return 'intrusion';
  if (t.includes('acceso')) return 'acceso';
  if (t.includes('incendio')) return 'incendio';
  if (t.includes('red')) return 'redes';
  return 'kit';
}

function resumenIncluye(kit: Kit, max = 3): { items: string[]; restantes: number } {
  const items = kit.productos.slice(0, max).map((p) => `${p.cantidad}× ${p.nombre_pto}`);
  const restantes = Math.max(0, kit.productos.length - max);
  return { items, restantes };
}

interface KitListCardProps {
  kit: Kit;
  className?: string;
}

/** Tarjeta de kit para listado /kits y home */
export function KitListCard({ kit, className }: KitListCardProps) {
  const addItem = useCartStore((s) => s.addItem);
  const precio = kit.precio_oferta ?? kit.precio;
  const sinStock = (kit.existencias_disponibles ?? 1) <= 0;
  const { items, restantes } = resumenIncluye(kit);
  const piezas = kit.productos.reduce((n, p) => n + p.cantidad, 0);

  return (
    <article
      className={cn(
        'flex h-full flex-col overflow-hidden rounded-xl border border-surface-200 bg-white shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      <Link
        to={`/kits/${kit.slug}`}
        className="relative block aspect-[16/10] overflow-hidden border-b border-surface-100"
      >
        <ProductImage
          imagen={kit.imagen}
          alt={kit.nombre}
          fallbackLabel={kit.nombre}
          categoria={kitCategoria(kit)}
          className="h-full w-full"
        />
        <span className="absolute left-3 top-3 rounded-md bg-white/95 px-2 py-1 text-[11px] font-semibold text-surface-800 shadow-sm">
          {piezas} piezas incluidas
        </span>
        {kit.precio_oferta && (
          <span className="badge-offer absolute right-3 top-3">Oferta</span>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <Link to={`/kits/${kit.slug}`}>
          <h2 className="text-base font-semibold leading-snug text-surface-900 hover:text-brand-700 sm:text-lg">
            {kit.nombre}
          </h2>
        </Link>
        <p className="mt-1.5 line-clamp-2 text-sm leading-relaxed text-surface-600">
          {kit.descripcion_web ?? kit.descripcion}
        </p>

        {items.length > 0 && (
          <ul className="mt-3 space-y-1 border-t border-surface-100 pt-3 text-sm text-surface-700">
            <li className="text-xs font-semibold uppercase tracking-wide text-surface-500">Incluye</li>
            {items.map((linea) => (
              <li key={linea} className="flex gap-2 leading-snug">
                <span className="text-brand-600">·</span>
                <span>{linea}</span>
              </li>
            ))}
            {restantes > 0 && (
              <li className="text-xs text-surface-500">y {restantes} producto{restantes > 1 ? 's' : ''} más</li>
            )}
          </ul>
        )}

        <div className="mt-auto flex flex-wrap items-end justify-between gap-3 border-t border-surface-100 pt-4">
          <PrecioDisplay
            precio={precio}
            precioLista={kit.precio_oferta ? kit.precio : null}
            variant="kit"
            etiquetaPrecio="Precio total del kit (sin IVA)"
          />
          <div className="flex flex-wrap gap-2">
            <Link to={`/kits/${kit.slug}`} className="btn-secondary px-3 py-2 text-sm">
              Ver kit
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <button
              type="button"
              disabled={sinStock}
              onClick={() =>
                addItem({
                  tipo: 'kit',
                  refId: kit.id_kit,
                  nombre: kit.nombre,
                  imagen: kit.imagen,
                  precio,
                  cantidad: 1,
                  esKit: true,
                })
              }
              className="btn-primary px-3 py-2 text-sm disabled:opacity-40"
            >
              <ShoppingCart className="h-4 w-4" />
              Agregar
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/** Tarjeta compacta para el home */
export function KitCard({ kit, className }: { kit: Kit; className?: string }) {
  return <KitListCard kit={kit} className={className} />;
}
